import { openModal, setModalWithData, useAppConfigurationSelector, useMsTeamsSelector, usePermissionsSelector, useTranslate } from "front";
import { useGroupsCache } from "../../hooks/useQuery/useGroupsCache";
import { IBehavior } from "../../interfaces/IBehavior";
import { IDialogs } from "../../interfaces/IDialog/IDialogs";
import { IContactsGroupBySearch, IGroup } from "../../interfaces/IGroup/IGroup";
import { useDispatch, useSelector } from "react-redux";
import { setGroupReducerState } from "../../redux/reducer/GroupReducer";
import { IAppDispatch, ReduxStoreState } from "../../redux/store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { translations } from "../../translations";
import { useParticipantsCache } from "hooks/useQuery/useParticipantsCache";

export const useDisplayByGroup = () => {
  const t = useTranslate(translations);
  const dispatchCtx = useDispatch<IAppDispatch>();
  const { appId } = useAppConfigurationSelector("appId");
  const { isOnMobile } = useMsTeamsSelector("isOnMobile");
  const { queryGroups } = useGroupsCache();
  const dispatch = useDispatch<IAppDispatch>();
  const [isShow, setIsShow] = useState<string[]>([]);
  const [contactsBySearch, setContactsBySearch] = useState<IContactsGroupBySearch[] | null>([]);
  const concernedGroup = useRef<string>();
  const { userPerm } = usePermissionsSelector("userPerm");
  const pagination = useSelector((s: ReduxStoreState) => s.pagination);
  const { queryParticipantsForGroupBySearch } = useParticipantsCache(undefined, true, pagination.searchPattern, pagination.firstLetter);

  useEffect(() => {
    if ((!queryGroups.isLoading && queryParticipantsForGroupBySearch && !queryParticipantsForGroupBySearch.isLoading) && (pagination.searchPattern.length > 0 || pagination.firstLetter.length > 0)) {
      
      Promise.all(
        queryGroups.data.map(async (group) => {
          const contacts = queryParticipantsForGroupBySearch?.data?.filter((c) => c.groupId.includes(group.id)) ?? [];
          return { ...group, contacts };
        })
      ).then((updatedContacts) => {
        setContactsBySearch(updatedContacts);
        setIsShow(updatedContacts.filter((g) => g.contacts.length !== 0).map((g) => g.id)); // ✅ Maintenant on est sûr que contactsBySearch a la bonne valeur
      });
    } else if (contactsBySearch && pagination.searchPattern.length === 0 && pagination.firstLetter.length === 0)
    {
      setIsShow(queryGroups.data.map((group) => { return (!group.participants) ? "" : group.id }));
      setContactsBySearch(null);
    }
  }, [queryGroups.isLoading, queryGroups.data, queryParticipantsForGroupBySearch?.isLoading, queryParticipantsForGroupBySearch?.data, pagination.searchPattern, pagination.firstLetter]);

  const handleModifyGroup = (data: IGroup, type: IBehavior) => {
    dispatch(setGroupReducerState({ state: type, data }));
    dispatch(openModal(IDialogs.GROUP));
  };

  const handleAccordionClick = (groupId: string) => {
    concernedGroup.current = groupId;
    const isHere = isShow.includes(groupId);
    if (isHere) {
      setIsShow((prev) => prev.filter((p) => p !== groupId));
    } else {
      setIsShow((prev) => [...prev, groupId]);
    }
  };

  const handleEdit = useCallback(() => {
    dispatchCtx(setModalWithData({ isOpen: IDialogs.GROUP, data: { isEditing: true, group: { id: concernedGroup.current } } }));
  }, []);

  const handleDelete = useCallback(() => {
    dispatchCtx(setModalWithData({ isOpen: IDialogs.DELETE_GROUP, data: { contactListId: appId, id: concernedGroup.current } }));
  }, []);

  const handleCopy = useCallback(() => {
    dispatchCtx(setModalWithData({ isOpen: IDialogs.COPY_GROUP, data: { id: concernedGroup.current } }));
  }, []);

  const cancelPropagation = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, groupId: string) => {
    e.stopPropagation();
    concernedGroup.current = groupId;
  };

  const sortedGroups = useMemo(() => {
    return [...queryGroups.data].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [queryGroups.data]);

  const sortedGroupsBySearch = useMemo(() => {
    if (!contactsBySearch) return null;
    return [...contactsBySearch].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [contactsBySearch])

  const openNewGroupDialog = useCallback(() => dispatch(setModalWithData({ isOpen: IDialogs.GROUP, data: { isEditing: false, group: undefined } })), []);

  return { data: sortedGroups, sortedGroupsBySearch, handleModifyGroup, handleAccordionClick, isShow, handleEdit, handleDelete, handleCopy, cancelPropagation, t, openNewGroupDialog, isOnMobile, userPerm };
};
